import { useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { meRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { type AppIconName, useAuthz, useOrgContext } from '@web/ui/core';
import { queryKeys } from '@web/core/query';

type SettingsSectionId =
  | 'profile'
  | 'members'
  | 'board'
  | 'triage'
  | 'schedule-changes'
  | 'traveler-access'
  | 'api-keys'
  | 'integrations';

interface SettingsSection {
  id: SettingsSectionId;
  label: string;
  icon: AppIconName;
  to:
    | '/orgs/$orgSlug/settings/profile'
    | '/orgs/$orgSlug/settings/members'
    | '/orgs/$orgSlug/settings/board'
    | '/orgs/$orgSlug/settings/triage'
    | '/orgs/$orgSlug/settings/schedule-changes'
    | '/orgs/$orgSlug/settings/traveler-access'
    | '/orgs/$orgSlug/settings/api-keys'
    | '/orgs/$orgSlug/settings/integrations';
}

interface SettingsGroup {
  id: 'account' | 'org';
  label: string;
  items: SettingsSection[];
}

export interface UseSettingsNavResult {
  groups: SettingsGroup[];
  active: SettingsSection;
  orgSlug: string;
}

/**
 * Settings sidebar nav model — derived from URL + permissions. Layout reads
 * `groups` for rendering and `active` for the breadcrumb/aria-current state.
 */
export function useSettingsNav(): UseSettingsNavResult {
  const { orgName, orgSlug } = useOrgContext();
  const authz = useAuthz();
  const location = useLocation();
  // /me is already cached by the org route loader chain; reading it
  // here is a hit unless the cache was invalidated.
  const { data: me } = useQuery({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });

  const profileSection: SettingsSection = {
    id: 'profile',
    label: t('settings.nav.profile'),
    icon: 'identity',
    to: '/orgs/$orgSlug/settings/profile',
  };
  const account: SettingsSection[] = [profileSection];

  // Org group order mirrors the v2 mockup: Membros → Kanban e tarefas →
  // Configuração de triagem. API keys is staff-only and absent from the
  // mockup, so it trails the mockup items.
  const org: SettingsSection[] = [];
  if (authz.has('accounts:members:read')) {
    org.push({
      id: 'members',
      label: t('settings.nav.members'),
      icon: 'users',
      to: '/orgs/$orgSlug/settings/members',
    });
  }
  if (authz.has('kanban:org_board_config:edit')) {
    org.push({
      id: 'board',
      label: t('settings.nav.board'),
      icon: 'squares',
      to: '/orgs/$orgSlug/settings/board',
    });
  }
  if (authz.has('kanban:triage_config:read')) {
    org.push({
      id: 'triage',
      label: t('settings.nav.triage'),
      icon: 'sliders',
      to: '/orgs/$orgSlug/settings/triage',
    });
  }
  if (authz.has('kanban:schedule_change:submit')) {
    org.push({
      id: 'schedule-changes',
      label: t('scheduleChanges.nav.label'),
      icon: 'airplane',
      to: '/orgs/$orgSlug/settings/schedule-changes',
    });
  }
  if (authz.has('kanban:traveler_access:audit')) {
    org.push({
      id: 'traveler-access',
      label: t('settings.nav.travelerAccess'),
      icon: 'shield-check',
      to: '/orgs/$orgSlug/settings/traveler-access',
    });
  }
  if (me?.isStaff) {
    org.push({
      id: 'api-keys',
      label: t('settings.nav.apiKeys'),
      icon: 'lock',
      to: '/orgs/$orgSlug/settings/api-keys',
    });
  }
  if (authz.has('kanban:integrations:read')) {
    org.push({
      id: 'integrations',
      label: t('settings.nav.integrations'),
      icon: 'link-simple',
      to: '/orgs/$orgSlug/settings/integrations',
    });
  }

  const groups: SettingsGroup[] = [
    { id: 'account', label: t('settings.nav.group.account'), items: account },
  ];
  if (org.length > 0) {
    groups.push({ id: 'org', label: orgName, items: org });
  }

  const all = groups.flatMap((g) => g.items);
  const fallback = all[0] ?? profileSection;
  const active = all.find((s) => location.pathname.endsWith(`/settings/${s.id}`)) ?? fallback;

  return { groups, active, orgSlug };
}
